<template>
  <el-row :gutter="15">
    <el-col :span="23">
      <el-row
        :gutter="8"
        class="pb-2"
      >
        <!-- Field -->
        <el-col :span="8">
          <el-select
            v-model="filter.field"
            filterable
            placeholder="Filter Type"
            size="small"
            :disabled="filter.disabled"
            class="w-100"
          >
            <el-option
              v-for="(f, index) in filterables"
              :key="index"
              :label="f.label"
              :value="f.field"
            />
          </el-select>
        </el-col>

        <!-- Operator -->
        <el-col :span="8">
          <el-select
            v-if="filter"
            v-model="filter.operator"
            size="small"
            value-key="operator"
            :disabled="filter.disabled || !filter.field"
            class="w-100"
          >
            <el-option
              v-for="(o, index) in operators"
              :key="index"
              :label="o.label"
              :value="o"
            />
          </el-select>
        </el-col>

        <!-- Value -->
        <el-col
          v-if="filter"
          :span="8"
        >
          <el-input
            v-if="operatorPredefinesValue"
            :key="1"
            readonly
            disabled
            size="small"
            class="w-100"
            @keyup.enter.native="$emit('apply')"
          />

          <el-select
            v-else-if="hasSelectableOptions"
            :key="2"
            v-model="filter.value"
            multiple
            placeholder="Please enter a keyword"
            size="small"
            :disabled="filter.disabled || !filter.operator"
            :loading="loading"
            class="w-100"
          >
            <template v-if="Array.isArray(instance.options)">
              <el-option
                v-for="(value, index) in instance.options"
                :key="index"
                :label="$filters.titleize(value)"
                :value="value"
              />
            </template>

            <template v-else>
              <el-option
                v-for="(value, label) in instance.options"
                :key="`${label}_${value}`"
                :label="$filters.titleize(label)"
                :value="value"
              />
            </template>
          </el-select>

          <el-input-number
            v-else-if="isNumberFieldType"
            :key="3"
            v-model="filter.value"
            controls-position="right"
            :disabled="filter.disabled || !filter.operator"
            size="small"
            @keyup.enter.native="$emit('apply')"
          />

          <el-input-number
            v-else-if="isIntegerFieldType"
            :key="4"
            v-model="filter.value"
            controls-position="right"
            :disabled="filter.disabled || !filter.operator"
            size="small"
            @keyup.enter.native="$emit('apply')"
          />

          <el-time-select
            v-else-if="isTimeFieldType"
            :key="5"
            v-model="filter.value"
            :is-range="isBetweenOperator"
            range-separator="To"
            start-placeholder="Start time"
            end-placeholder="End time"
            placeholder="Start time"
            size="small"
            :disabled="filter.disabled || !filter.operator"
            class="w-100"
          />

          <el-date-picker
            v-else-if="isDateFieldType"
            :key="6"
            v-model="filter.value"
            format="dd/MM/yyyy"
            :type="(isBetweenOperator ? 'daterange' : 'date')"
            range-separator="To"
            start-placeholder="Start date"
            end-placeholder="End date"
            size="small"
            :disabled="filter.disabled || !filter.operator"
            class="w-100"
          />

          <el-date-picker
            v-else-if="isDateTimeFieldType"
            :key="7"
            v-model="filter.value"
            format="dd/MM/yyyy HH:mm"
            :type="(isBetweenOperator ? 'datetimerange' : 'datetime')"
            range-separator="To"
            start-placeholder="Start date"
            end-placeholder="End date"
            size="small"
            :disabled="filter.disabled || !filter.operator"
            class="w-100"
          />

          <el-select
            v-else-if="!hasSelectableOptions && (isCollectionFieldType || isItemFieldType)"
            :key="8"
            v-model="filter.value"
            multiple
            filterable
            remote
            default-first-option
            value-key="id"
            placeholder="Please enter a keyword"
            size="small"
            class="w-100"
            :remote-method="search"
            :loading="loading"
            :disabled="filter.disabled || !filter.operator"
          >
            <el-option
              v-for="selectable in selectables"
              :key="selectable.id"
              :label="selectable.label"
              :value="selectable"
            />
          </el-select>

          <el-input
            v-else
            :key="9"
            v-model="filter.value"
            clearable
            :disabled="filter.disabled || !filter.operator"
            size="small"
            class="w-100"
            @keyup.enter.native="$emit('apply')"
          />
        </el-col>
      </el-row>
    </el-col>

    <el-col
      :span="1"
      class="text-center"
    >
      <el-button
        type="danger"
        icon="el-icon-delete"
        circle
        size="mini"
        :disabled="filter.disabled"
        @click.native="$emit('delete')"
      />
    </el-col>
  </el-row>
</template>

<script>
import Filter from '@/plugins/sdk/Filter'
import Resource from '@/plugins/sdk/Resource'
import {
  BEGINS_WITH,
  CONTAINS,
  DOES_NOT_BEGIN_WITH,
  DOES_NOT_CONTAIN,
  DOES_NOT_END_IN,
  DOES_NOT_EQUAL,
  ENDS_IN,
  EQUALS,
  IS,
  IS_BETWEEN,
  IS_EMPTY,
  IS_FALSE,
  IS_GREATER_THAN,
  IS_LESS_THAN,
  IS_NOT,
  IS_NOT_BETWEEN,
  IS_TRUE
} from '@/plugins/sdk/operators'
import {
  ARRAY,
  BOOLEAN,
  DATE,
  DATETIME,
  INTEGER,
  COLLECTION,
  NUMBER,
  OBJECT,
  ITEM,
  STRING,
  TIME,
  UUID
} from '@/plugins/sdk/fields'

export default {
  props: {
    resource: {
      type: [ Object, Function ],
      required: true
    },
    filter: {
      type: Object,
      required: true,
      validator: function (value) {
        return value instanceof Filter
      }
    },
    filterables: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      selectables: []
    }
  },
  computed: {
    /**
     * Returns the sdk filter resource instance.
     */
    instance () {
      let resource

      if (typeof this.filter.field !== 'undefined') {
        resource = this
          .filterables
          .find(filterable => filterable.field === this.filter.field)
      }

      return resource
    },
    field () {
      let resource = this.resource
      let field

      if (typeof this.filter.field !== 'undefined') {
        this
          .filter
          .field
          .split('.')
          .map(f => {
            if (resource instanceof Resource) {
              field = resource.constructor.fields[f]
            } else if (resource.prototype instanceof Resource) {
              field = resource.fields[f]
            }

            resource = field._resource || resource
          })
      }

      return field
    },
    /**
     * Gets the operators compatible with the filtered field.
     */
    operators () {
      if (this.hasSelectableOptions) {
        return [ IS, IS_NOT ]
      } else {
        return [
          CONTAINS,
          DOES_NOT_CONTAIN,
          IS,
          IS_NOT,
          BEGINS_WITH,
          DOES_NOT_BEGIN_WITH,
          ENDS_IN,
          DOES_NOT_END_IN,
          EQUALS,
          DOES_NOT_EQUAL,
          IS_BETWEEN,
          IS_NOT_BETWEEN,
          IS_GREATER_THAN,
          IS_LESS_THAN,
          IS_TRUE,
          IS_FALSE,
          IS_EMPTY
        ].filter(operator => !!operator.fields.find(field => this.field instanceof field))
      }
    },
    /**
     * Determines if the field type is array.
     */
    isArrayFieldType () {
      return this.field instanceof ARRAY
    },
    /**
     * Determines if the field type is boolean.
     */
    isBooleanFieldType () {
      return this.field instanceof BOOLEAN
    },
    /**
     * Determines if the field type is date.
     */
    isDateFieldType () {
      return this.field instanceof DATE
    },
    /**
     * Determines if the field type is date time.
     */
    isDateTimeFieldType () {
      return this.field instanceof DATETIME
    },
    /**
     * Determines if the field type is integer.
     */
    isIntegerFieldType () {
      return this.field instanceof INTEGER
    },
    /**
     * Determines if the field type is collection.
     */
    isCollectionFieldType () {
      return this.field instanceof COLLECTION
    },
    /**
     * Determines if the field type is number.
     */
    isNumberFieldType () {
      return this.field instanceof NUMBER
    },
    /**
     * Determines if the field type is object.
     */
    isObjectFieldType () {
      return this.field instanceof OBJECT
    },
    /**
     * Determines if the field type is item.
     */
    isItemFieldType () {
      return this.field instanceof ITEM
    },
    /**
     * Determines if the field type is string.
     */
    isStringFieldType () {
      return this.field instanceof STRING
    },
    /**
     * Determines if the field type is time.
     */
    isTimeFieldType () {
      return this.field instanceof TIME
    },
    /**
     * Determines if the field type is uuid.
     */
    isUuidFieldType () {
      return this.field instanceof UUID
    },
    /**
     * Determines if the selected operator is a between operator.
     */
    isBetweenOperator () {
      return this.instanceOfBetweenOperator(this.filter.operator)
    },
    /**
     * Determines if the selected operator is a boolean operator.
     */
    isBooleanOperator () {
      return false
    },
    /**
     * Determines if the selected operator is an empty operator.
     */
    isEmptyOperator () {
      return this.instanceOfEmptyOperator(this.filter.operator)
    },
    /**
     * Determines if the filter instance has predefined options.
     */
    hasSelectableOptions () {
      let has = false

      if (this.instance) {
        has = Object.keys(this.instance.options).length > 0
      }

      return has
    },
    /**
     * Determines if the selected operator predefines a value.
     */
    operatorPredefinesValue () {
      return this.isBooleanOperator || this.isEmptyOperator
    }
  },
  watch: {
    'filter.field': function () {
      this.filter.operator = this.operators[0]
    },
    'filter.operator': function (newOperator, previousOperator) {
      let compatible = true

      if ((this.isBetweenOperator && !this.instanceOfBetweenOperator(previousOperator)) ||
        (this.isBooleanOperator && !this.instanceOfBooleanOperator(previousOperator)) ||
        (this.isEmptyOperator && !this.instanceOfEmptyOperator(previousOperator))) {
        compatible = false
      }

      if (!compatible) {
        this.filter.value = undefined
      }
    }
  },
  created () {
    if ((!this.hasSelectableOptions && (this.isCollectionFieldType || this.isItemFieldType)) && Array.isArray(this.filter.value)) {
      this.filter.value.map(resource => this.selectables.push(resource))
    }
  },
  methods: {
    search (query) {
      if (query !== '') {
        this.loading = true

        this
          .$sdk
          .search(query, this.field._resource)
          .then(result => { this.selectables = result })
          .finally(() => { this.loading = false })
      }
    },
    instanceOfBetweenOperator (operator) {
      let is = false

      if (operator) {
        is = [
          IS_BETWEEN.operator,
          IS_NOT_BETWEEN.operator
        ].includes(operator.operator)
      }

      return is
    },
    instanceOfBooleanOperator (operator) {
      let is = false

      if (operator) {
        is = [
          IS_TRUE.operator,
          IS_FALSE.operator
        ].includes(operator.operator)
      }

      return is
    },
    instanceOfEmptyOperator (operator) {
      let is = false

      if (operator) {
        is = (operator.operator === IS_EMPTY.operator)
      }

      return is
    }
  }
}
</script>
